/**
 * Send Verification Actor
 *
 * Sends OTP verification via SMS (phone) or email based on contact type.
 * Returns neverthrow Result with typed success/error.
 */

import type { LanguageCodeWithFrench } from "@/app/common/constants";
import { datadogLogs } from "@datadog/browser-logs";
import { fromPromise } from "xstate";
import { z } from "zod";
import { requestAndSendOTPMessage } from "../../server-actions/otp-actions";
import { findConsumerByNameAndDob } from "../../server-actions/user-actions";

const InsuranceSchema = z.object({
  insuranceId: z.string().uuid(),
  memberId: z.string().optional(),
  payer: z.string().optional(),
  state: z.string().optional(),
  ehrInsuranceId: z.string().optional().nullable(),
  priority: z.number().optional(),
});

type MinimalInsurance = z.infer<typeof InsuranceSchema>;

/**
 * Input Schema (What this actor needs)
 */
const SendSMSInputSchema = z
  .object({
    // Contact info - either phone or email
    phoneNumber: z
      .string()
      .regex(/^\+?[1-9]\d{1,14}$/, "Invalid phone format")
      .optional(),
    email: z.string().optional(),
    // User lookup fields (if userId not provided)
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    dob: z.string().optional(),
    orgId: z.number().optional(),
    orgName: z.string(),
    language: z.string(),
    // Or direct userId
    userId: z.string().optional(),
  })
  .refine((data) => data.phoneNumber || data.email, {
    message: "Either phoneNumber or email must be provided",
  });

type SendSMSInput = z.infer<typeof SendSMSInputSchema>;

/**
 * Success Schema (What this actor returns on success)
 */
const SendSMSSuccessSchema = z.object({
  otpTime: z.string().nullable(), // OTP timestamp (renamed from 'time' to match context field)
  userId: z.string().nullable(),
  consumerId: z.string().nullable(),
  phoneNumber: z.string().optional(),
  insurances: z
    .object({
      primaryInsurance: InsuranceSchema.optional(),
      secondaryInsurance: InsuranceSchema.optional(),
    })
    .optional()
    .default({}),
  ehrPatientId: z.string().optional().nullable(),
  email: z.string().optional(),
  contactType: z.enum(["phone", "email"]),
});

type SendSMSSuccess = z.infer<typeof SendSMSSuccessSchema>;

/**
 * Error Schema (What this actor returns on failure)
 */
const SendSMSErrorSchema = z.object({
  code: z.enum([
    "INVALID_CONTACT",
    "INVALID_PHONE",
    "INVALID_EMAIL",
    "USER_LOOKUP_REQUIRED",
    "USER_NOT_FOUND",
    "OTP_REQUEST_FAILED",
    "MESSAGE_CONTENT_ERROR",
    "CONSUMER_NOT_FOUND",
    "OTP_SEND_FAILED",
    "EMAIL_SEND_FAILED",
    "GRAPHQL_ERROR",
    "NETWORK_ERROR",
  ]),
  message: z.string(),
  retryable: z.boolean(),
});

type SendSMSError = z.infer<typeof SendSMSErrorSchema>;

/**
 * Dependencies (injected at runtime)
 */
export interface SendSMSDependencies {
  analytics?: {
    track: (event: string, properties?: Record<string, unknown>) => void;
  };
}

/**
 * Actor metadata
 */
export interface ActorMetadata {
  displayName: string;
  description: string;
  category: "communication" | "data" | "auth" | "unknown";
  retryable: boolean;
  timeout: number;
  errorCodes: readonly string[];
  inputSchema: z.ZodSchema;
  successSchema: z.ZodSchema;
  errorSchema: z.ZodSchema;
  onSuccess: (result: unknown) => Record<string, unknown>;
  onError: (error: unknown) => Record<string, unknown>;
  composedActors?: readonly string[];
}

/**
 * Actor with metadata wrapper
 * Using 'any' for impl to avoid XState's complex type inference issues
 */
export interface ActorWithMetadata {
  // biome-ignore lint/suspicious/noExplicitAny: XState actor types are too complex for static typing
  impl: any;
  metadata: ActorMetadata;
}

/**
 * Send SMS Actor Implementation
 *
 * NOTE: XState compatibility - we unwrap neverthrow Results here:
 * - throw error → XState onError transition
 * - return value → XState onDone transition
 */
const sendSMSActorImpl = fromPromise(
  async ({
    input,
  }: {
    input: SendSMSInput & { deps: SendSMSDependencies };
  }): Promise<SendSMSSuccess> => {
    const {
      phoneNumber,
      email,
      firstName,
      lastName,
      dob,
      orgId,
      orgName,
      language,
      userId: providedUserId,
      deps,
    } = input;

    // Determine contact type
    const contactType: "phone" | "email" = phoneNumber ? "phone" : "email";

    // Validate input
    const validated = SendSMSInputSchema.safeParse({
      phoneNumber,
      email,
      firstName,
      lastName,
      dob,
      orgId,
      orgName,
      language,
      userId: providedUserId,
    });

    if (!validated.success) {
      const errorData: SendSMSError = {
        code: contactType === "phone" ? "INVALID_PHONE" : "INVALID_EMAIL",
        message: `Validation failed: ${validated.error.message}`,
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error; // XState onError
    }

    // Step 1: Get userId (either provided or look it up)
    let userId = providedUserId;
    let consumerId: string | undefined = undefined;
    let ehrPatientId: string | null | undefined = undefined;
    let insurances: MinimalInsurance[] = [];
    let existingUserPhone: string | undefined | null = null;
    let existingUserEmail: string | undefined | null = null;

    if (!userId) {
      // Need to look up user
      if (!firstName || !lastName || !dob || !orgId) {
        const error: SendSMSError = {
          code: "USER_LOOKUP_REQUIRED",
          message:
            "Either userId or (firstName, lastName, dob, orgId) must be provided",
          retryable: false,
        };
        throw error; // XState onError
      }

      const result = await findConsumerByNameAndDob(
        firstName,
        lastName,
        dob,
        orgId,
      );

      /**
       * If new user, don't do anything
       */
      if (!result.success) {
        const errorData: SendSMSError = {
          code: result.error.code as SendSMSError["code"],
          message: result.error.message,
          retryable: result.error.retryable,
        };
        const success: SendSMSSuccess = {
          otpTime: null,
          userId: null,
          consumerId: null,
          ehrPatientId: null,
          insurances: {},
          phoneNumber,
          email,
          contactType,
        };
        return success; // XState onDone
      }

      userId = result.data.user.id;
      consumerId = result.data.id;
      existingUserPhone = result.data.user.phone;
      existingUserEmail = result.data.user.email;
      insurances = result.data.insurances.map((ins) =>
        InsuranceSchema.parse({
          insuranceId: ins.id,
          memberId: ins.memberId,
          payer: ins.payer,
          state: ins.state,
          ehrInsuranceId: ins.ehrInsuranceId,
          priority: ins.priority,
        }),
      );
      ehrPatientId = result.data.ehrPatientId;
    }

    const sendToPhone =
      contactType === "phone"
        ? phoneNumber?.replace(/\D/g, "") || ""
        : existingUserPhone?.replace(/\D/g, "") || "";

    const sendToEmail =
      contactType === "email" ? email || "" : existingUserEmail || "";

    // Step 2: Request and send OTP (phone or email)
    const otpResult = await requestAndSendOTPMessage(
      userId,
      orgName,
      sendToPhone,
      sendToEmail,
      language as LanguageCodeWithFrench,
    );

    if (!otpResult.success) {
      const errorData: SendSMSError = {
        code: otpResult.error.code as SendSMSError["code"],
        message: otpResult.error.message,
        retryable: otpResult.error.retryable,
      };
      const err = new Error(errorData.message);
      Object.assign(err, errorData);
      throw err; // XState onError
    }

    const success: SendSMSSuccess = {
      otpTime: otpResult.data.time,
      userId: otpResult.data.userId,
      ehrPatientId: ehrPatientId as string,
      insurances: {
        primaryInsurance:
          insurances.find((i) => i.priority === 1) ?? insurances[0],
        ...(insurances.length > 1 && {
          secondaryInsurance:
            insurances.find((i) => i.priority === 2) ?? insurances[1],
        }),
      },
      consumerId: consumerId as string,
      // Include missing contact info if we retrieved it from user lookup
      phoneNumber:
        contactType === "phone"
          ? phoneNumber
          : existingUserPhone || phoneNumber,
      email:
        contactType === "email"
          ? email || ""
          : existingUserEmail || email || undefined,
      contactType,
    };
    if (consumerId) {
      datadogLogs.logger.info("kiosk consumer identified", {
        context: {
          action: "kiosk_consumer_identified",
          metadata: {
            consumerId,
            orgId: validated.data.orgId ?? null,
            identifiedVia: "returning_patient",
          },
        },
      });
    }
    return success; // XState onDone
  },
);

/**
 * Send Verification Actor with Metadata
 */
export const sendSMSActor: ActorWithMetadata = {
  impl: sendSMSActorImpl,
  metadata: {
    displayName: "sendSMS",
    description:
      "Send OTP verification via SMS or email (looks up user if needed)",
    category: "communication",
    retryable: true,
    timeout: 15000, // 15 seconds (includes user lookup + OTP send)
    errorCodes: [
      "INVALID_CONTACT",
      "INVALID_PHONE",
      "INVALID_EMAIL",
      "USER_LOOKUP_REQUIRED",
      "USER_NOT_FOUND",
      "OTP_REQUEST_FAILED",
      "MESSAGE_CONTENT_ERROR",
      "CONSUMER_NOT_FOUND",
      "OTP_SEND_FAILED",
      "EMAIL_SEND_FAILED",
      "GRAPHQL_ERROR",
      "NETWORK_ERROR",
    ],
    inputSchema: SendSMSInputSchema,
    successSchema: SendSMSSuccessSchema,
    errorSchema: SendSMSErrorSchema,
    onSuccess: (result: unknown) => {
      const success = result as SendSMSSuccess;
      return {
        otpTime: success.otpTime,
        userId: success.userId,
        phoneNumber: success.phoneNumber,
        email: success.email,
        contactType: success.contactType,
        consumerId: success.consumerId,
        smsError: undefined,
      };
    },
    onError: (error: unknown) => {
      const err = error as SendSMSError;
      return {
        smsError: {
          code: err.code,
          message: err.message,
          retryable: err.retryable,
        },
      };
    },
  },
};
